import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import LogLayout from "../components/LogLayout";
import {
  Crown, Users, Shield, ToggleLeft, ToggleRight, CreditCard,
  RefreshCw, Check, X, ChevronDown, ChevronUp, Zap, Eye, Settings, Package, Clock, Star,
  Send, CheckCircle2, XCircle, AlertTriangle, Hourglass
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;
const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n || 0));

const PLAN_COLORS = { basique: "#64748b", essentiel: "#3b82f6", business: "#f59e0b", professionnel: "#8b5cf6" };
const SERVICE_LABELS = { cmes_log: "CMES-LOG", intel_stock: "CMES INTEL-STOCK", pack_pro: "Pack CMES Pro (les 2)" };
const STATUS_STYLES = {
  active: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  suspended: "bg-red-500/100/20 text-red-400 border-red-500/30",
  cancelled: "bg-white/50/20 text-slate-400 border-slate-500/30",
  trial: "bg-sky-500/20 text-sky-400 border-sky-500/30",
};
const REQUEST_STATUS = {
  pending_approval: { label: "En attente d'approbation", color: "text-amber-400", bg: "bg-amber-500/100/10 border-amber-500/20", icon: Hourglass },
  approved: { label: "Approuve - En attente de paiement", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20", icon: CheckCircle2 },
  rejected: { label: "Refusée", color: "text-red-400", bg: "bg-red-500/100/10 border-red-500/20", icon: XCircle },
  completed: { label: "Complétée", color: "text-sky-400", bg: "bg-sky-500/10 border-sky-500/20", icon: Check },
};
const FEATURE_ICONS = {
  max_users: Users, max_invoices_month: Package, ocr_scans_month: Eye,
  ai_reports: Zap, gps_tracking: Shield, pipeline_kanban: Settings,
  exports_excel: Package, exports_pdf: Package, messagerie_assureurs: Package,
  api_pos: Settings, multi_sites: Shield,
};

export default function LogSubscriptionsPage() {
  const [plans, setPlans] = useState({});
  const [featureLabels, setFeatureLabels] = useState({});
  const [clients, setClients] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [mySub, setMySub] = useState(null);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(false);
  const [assignForm, setAssignForm] = useState({ pharmacy_id: "", plan_id: "essentiel", service_type: "pack_pro" });
  const [expandedClient, setExpandedClient] = useState(null);
  const [activeTab, setActiveTab] = useState("plans");
  const [selectedService, setSelectedService] = useState("pack_pro");
  const [submitting, setSubmitting] = useState(false);
  const { user: authUser } = useAuth();
  const user = authUser || {};
  const isSuperAdmin = user?.role === "superadmin";

  const pollPayment = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 5) { toast.error("Vérification du paiement expiree."); return; }
    try {
      const res = await axios.get(`${API}/subscriptions/checkout/status/${sessionId}`, { headers: getHeaders() });
      if (res.data.payment_status === "paid") { toast.success("Paiement réussi ! Abonnement actif."); }
      else if (res.data.status === "expired") { toast.error("Session expiree."); }
      else { setTimeout(() => pollPayment(sessionId, attempts + 1), 2000); }
    } catch { toast.error("Erreur vérification paiement"); }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (sessionId) { pollPayment(sessionId); window.history.replaceState({}, "", window.location.pathname); }
    if (params.get("cancelled")) { toast.error("Paiement annule"); window.history.replaceState({}, "", window.location.pathname); }
  }, [pollPayment]);

  const load = useCallback(async () => {
    try {
      const promises = [
        axios.get(`${API}/subscriptions/plans`, { headers: getHeaders() }),
        axios.get(`${API}/subscriptions/my`, { headers: getHeaders() }),
      ];
      if (isSuperAdmin) {
        promises.push(axios.get(`${API}/subscriptions/clients`, { headers: getHeaders() }));
        promises.push(axios.get(`${API}/superadmin/accounts`, { headers: getHeaders() }).catch(() => ({ data: [] })));
        promises.push(axios.get(`${API}/subscriptions/requests`, { headers: getHeaders() }));
      }
      const results = await Promise.all(promises);
      setPlans(results[0].data.plans || {});
      setFeatureLabels(results[0].data.feature_labels || {});
      setMySub(results[1].data.subscription);
      setPendingRequest(results[1].data.pending_request || null);
      if (isSuperAdmin) {
        setClients(results[2].data.subscriptions || []);
        const userList = Array.isArray(results[3].data) ? results[3].data : (results[3].data.accounts || []);
        setAllUsers(userList.filter(u => u.role === "pharmacy"));
        setRequests((results[4]?.data?.requests || []).filter(r => ["pending_approval", "approved"].includes(r.status)));
      }
    } catch { }
    finally { setLoading(false); }
  }, [isSuperAdmin]);

  useEffect(() => { load(); }, [load]);

  const assignPlan = async () => {
    if (!assignForm.pharmacy_id || !assignForm.plan_id) { toast.error("Selectionnez un client et un plan"); return; }
    try {
      await axios.put(`${API}/subscriptions/client`, {
        pharmacy_id: assignForm.pharmacy_id, plan_id: assignForm.plan_id,
        service_type: assignForm.service_type, status: "active",
      }, { headers: getHeaders() });
      toast.success("Abonnement attribue !");
      setShowAssign(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const toggleFeature = async (pharmacyId, featureKey, currentValue) => {
    try {
      await axios.put(`${API}/subscriptions/toggle-feature`, {
        pharmacy_id: pharmacyId, feature_key: featureKey, enabled: !currentValue,
      }, { headers: getHeaders() });
      toast.success(`${featureLabels[featureKey] || featureKey} ${!currentValue ? "active" : "desactive"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const toggleService = async (pharmacyId, newStatus) => {
    try {
      await axios.put(`${API}/subscriptions/toggle-service`, { pharmacy_id: pharmacyId, status: newStatus }, { headers: getHeaders() });
      toast.success(`Service ${newStatus === "active" ? "active" : "suspendu"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // Client: submit subscription request
  const submitRequest = async (planId, serviceType) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/subscriptions/request`, { plan_id: planId, service_type: serviceType }, { headers: getHeaders() });
      toast.success("Demande soumise ! Le SuperAdmin va la valider.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de la demande"); }
    finally { setSubmitting(false); }
  };

  // Client: pay after approval
  const initiatePayment = async (planId, serviceType) => {
    try {
      const res = await axios.post(`${API}/subscriptions/checkout`, {
        plan_id: planId, service_type: serviceType, origin_url: window.location.origin,
      }, { headers: getHeaders() });
      if (res.data.url) window.location.href = res.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de paiement"); }
  };

  // SuperAdmin: approve or reject a request
  const actionRequest = async (requestId, action, reason) => {
    try {
      await axios.put(`${API}/subscriptions/requests/action`, {
        request_id: requestId, action, reason: reason || "",
      }, { headers: getHeaders() });
      toast.success(action === "approve" ? "Demande approuvee !" : "Demande refusee.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // SuperAdmin: manually activate a subscription (cash payment)
  const activateManually = async (requestId, paymentMethod) => {
    try {
      const notes = paymentMethod === "cash" ? "Paiement en espèces" : paymentMethod === "virement" ? "Virement bancaire" : "Chèque";
      await axios.put(`${API}/subscriptions/requests/activate`, {
        request_id: requestId, payment_method: paymentMethod, notes,
      }, { headers: getHeaders() });
      toast.success("Abonnement active avec succès !");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'activation"); }
  };

  // Trial countdown
  const trialDaysLeft = mySub?.status === "trial" && mySub?.created_at
    ? Math.max(0, (mySub.features?.trial_days || 30) - Math.floor((Date.now() - new Date(mySub.created_at).getTime()) / 86400000))
    : null;

  if (loading) return (
    <LogLayout title="Abonnements" subtitle=""><div className="flex justify-center py-20"><RefreshCw className="w-6 h-6 text-sky-400 animate-spin" /></div></LogLayout>
  );

  return (
    <LogLayout title="Abonnements & Facturation" subtitle="CMES-LOG et CMES INTEL-STOCK">
      <div data-testid="subscriptions-page" className="space-y-5">

        {/* Trial counter banner */}
        {trialDaysLeft !== null && (
          <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/25 rounded-2xl p-5 flex items-center gap-4" data-testid="trial-banner">
            <div className="w-14 h-14 rounded-2xl bg-amber-500/100/20 flex items-center justify-center flex-shrink-0">
              <Clock className="w-7 h-7 text-amber-400" />
            </div>
            <div className="flex-1">
              <p className="text-base font-bold text-amber-400">Période d'essai gratuit</p>
              <p className="text-sm text-slate-400 mt-0.5">Il vous reste <span className="text-white font-bold">{trialDaysLeft} jour{trialDaysLeft !== 1 ? "s" : ""}</span> d'essai sur la formule <span className="font-semibold" style={{ color: PLAN_COLORS[mySub?.plan_id] }}>{mySub?.plan_name}</span></p>
            </div>
            <Button onClick={() => setActiveTab("plans")} className="bg-amber-500/100 hover:bg-amber-400 text-black font-bold text-sm px-5" data-testid="upgrade-btn">
              <Star className="w-4 h-4 mr-1.5" /> Passer a la version payante
            </Button>
          </div>
        )}

        {/* Pending request banner (non-superadmin) */}
        {!isSuperAdmin && pendingRequest && (
          <div className={`border rounded-2xl p-5 flex items-center gap-4 ${REQUEST_STATUS[pendingRequest.status]?.bg || "bg-slate-900/60 border-white/5"}`} data-testid="pending-request-banner">
            {(() => { const Icon = REQUEST_STATUS[pendingRequest.status]?.icon || Hourglass; return <Icon className={`w-7 h-7 flex-shrink-0 ${REQUEST_STATUS[pendingRequest.status]?.color || "text-slate-400"}`} />; })()}
            <div className="flex-1">
              <p className={`text-base font-bold ${REQUEST_STATUS[pendingRequest.status]?.color || "text-slate-400"}`}>
                {REQUEST_STATUS[pendingRequest.status]?.label || pendingRequest.status}
              </p>
              <p className="text-sm text-slate-400 mt-0.5">
                Formule <span className="font-semibold" style={{ color: PLAN_COLORS[pendingRequest.plan_id] }}>{pendingRequest.plan_name}</span> — {SERVICE_LABELS[pendingRequest.service_type] || pendingRequest.service_type} — {fmt(pendingRequest.price)} FCFA/mois
              </p>
            </div>
            {pendingRequest.status === "approved" && (
              <Button onClick={() => initiatePayment(pendingRequest.plan_id, pendingRequest.service_type)}
                className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold text-sm px-5" data-testid="pay-approved-btn">
                <CreditCard className="w-4 h-4 mr-1.5" /> Payer maintenant
              </Button>
            )}
          </div>
        )}

        {/* Current subscription info (non-superadmin) */}
        {!isSuperAdmin && mySub && (
          <div className="bg-slate-900/60 border border-white/5 rounded-xl p-5 flex items-center gap-4" data-testid="current-subscription">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: (PLAN_COLORS[mySub.plan_id] || "#64748b") + "20" }}>
              <Crown className="w-6 h-6" style={{ color: PLAN_COLORS[mySub.plan_id] || "#64748b" }} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-white">Votre abonnement : <span style={{ color: PLAN_COLORS[mySub.plan_id] }}>{mySub.plan_name}</span></p>
              <p className="text-xs text-slate-400">{SERVICE_LABELS[mySub.service_type] || mySub.service_type} — {fmt(mySub.price)} FCFA/mois</p>
            </div>
            <span className={`text-xs px-3 py-1 rounded-full border font-medium ${STATUS_STYLES[mySub.status] || STATUS_STYLES.active}`}>{mySub.status === "active" ? "Actif" : mySub.status === "trial" ? "Essai gratuit" : mySub.status}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex gap-1 bg-slate-900/60 border border-white/5 rounded-xl p-1">
            <button onClick={() => setActiveTab("plans")} data-testid="tab-plans"
              className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${activeTab === "plans" ? "bg-sky-500 text-white" : "text-slate-400 hover:text-white"}`}>
              <Crown className="w-3.5 h-3.5 inline mr-1" /> Formules
            </button>
            {isSuperAdmin && (
              <>
                <button onClick={() => setActiveTab("requests")} data-testid="tab-requests"
                  className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all relative ${activeTab === "requests" ? "bg-amber-500/100 text-white" : "text-slate-400 hover:text-white"}`}>
                  <Send className="w-3.5 h-3.5 inline mr-1" /> Demandes
                  {requests.length > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500/100 text-white text-[9px] rounded-full flex items-center justify-center font-bold">{requests.length}</span>}
                </button>
                <button onClick={() => setActiveTab("clients")} data-testid="tab-clients"
                  className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${activeTab === "clients" ? "bg-violet-500 text-white" : "text-slate-400 hover:text-white"}`}>
                  <Users className="w-3.5 h-3.5 inline mr-1" /> Clients
                </button>
              </>
            )}
          </div>

          {/* Service type selector */}
          {activeTab === "plans" && (
            <div className="flex gap-1 bg-slate-900/60 border border-white/5 rounded-xl p-1">
              {[
                { v: "pack_pro", l: "Pack Pro (les 2)", badge: "-20%" },
                { v: "cmes_log", l: "CMES-LOG seul" },
                { v: "intel_stock", l: "INTEL-STOCK seul" },
              ].map(s => (
                <button key={s.v} onClick={() => setSelectedService(s.v)} data-testid={`service-${s.v}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${selectedService === s.v ? "bg-emerald-500 text-white" : "text-slate-400 hover:text-white"}`}>
                  {s.l}
                  {s.badge && <span className="text-[9px] bg-red-500/100 text-white px-1.5 py-0.5 rounded-full font-bold">{s.badge}</span>}
                </button>
              ))}
            </div>
          )}

          {isSuperAdmin && activeTab === "clients" && (
            <Button onClick={() => setShowAssign(!showAssign)} size="sm" className="bg-violet-500 hover:bg-violet-400 text-white text-xs" data-testid="assign-plan-btn">
              <Crown className="w-3.5 h-3.5 mr-1" /> Attribuer
            </Button>
          )}
        </div>

        {/* Assign form */}
        {showAssign && isSuperAdmin && (
          <div className="bg-violet-500/5 border border-violet-500/20 rounded-xl p-4 space-y-3">
            <h3 className="text-sm font-bold text-violet-400">Attribuer un abonnement</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <select value={assignForm.pharmacy_id} onChange={e => setAssignForm(p => ({ ...p, pharmacy_id: e.target.value }))}
                className="bg-slate-900/60 border border-white/10 rounded-lg text-white text-sm h-9 px-3" data-testid="assign-client">
                <option value="">-- Client --</option>
                {allUsers.map(u => <option key={u.id} value={u.id}>{u.pharmacy_name || u.full_name || u.email}</option>)}
              </select>
              <select value={assignForm.plan_id} onChange={e => setAssignForm(p => ({ ...p, plan_id: e.target.value }))}
                className="bg-slate-900/60 border border-white/10 rounded-lg text-white text-sm h-9 px-3" data-testid="assign-plan">
                {Object.entries(plans).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
              </select>
              <select value={assignForm.service_type} onChange={e => setAssignForm(p => ({ ...p, service_type: e.target.value }))}
                className="bg-slate-900/60 border border-white/10 rounded-lg text-white text-sm h-9 px-3" data-testid="assign-service">
                <option value="pack_pro">Pack CMES Pro (les 2)</option>
                <option value="cmes_log">CMES-LOG seul</option>
                <option value="intel_stock">CMES INTEL-STOCK seul</option>
              </select>
              <Button onClick={assignPlan} className="bg-violet-500 hover:bg-violet-400 text-white text-sm h-9" data-testid="confirm-assign">Attribuer</Button>
            </div>
          </div>
        )}

        {/* ===== PLANS TAB ===== */}
        {activeTab === "plans" && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="plans-grid">
            {Object.entries(plans).map(([planId, plan]) => {
              const isCurrentPlan = mySub?.plan_id === planId;
              const hasPendingForThis = pendingRequest?.plan_id === planId && ["pending_approval", "approved"].includes(pendingRequest?.status);
              const isApproved = pendingRequest?.plan_id === planId && pendingRequest?.status === "approved";
              const price = plan.prices?.[selectedService] || plan.prices?.pack_pro || 0;
              return (
                <div key={planId} data-testid={`plan-${planId}`}
                  className={`bg-slate-900/60 border rounded-2xl overflow-hidden flex flex-col transition-all ${isCurrentPlan ? "border-emerald-500/40 ring-1 ring-emerald-500/20" : hasPendingForThis ? "border-amber-500/40 ring-1 ring-amber-500/20" : "border-white/5 hover:border-white/15"}`}
                  style={{ borderTopColor: plan.color, borderTopWidth: 3 }}>
                  <div className="p-5 text-center relative">
                    {isCurrentPlan && <span className="absolute top-3 right-3 text-[9px] bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full border border-emerald-500/30 font-bold">ACTUEL</span>}
                    {hasPendingForThis && !isCurrentPlan && (
                      <span className="absolute top-3 right-3 text-[9px] bg-amber-500/100/20 text-amber-400 px-2 py-0.5 rounded-full border border-amber-500/30 font-bold">DEMANDE EN COURS</span>
                    )}
                    <Crown className="w-8 h-8 mx-auto mb-2" style={{ color: plan.color }} />
                    <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                    <p className="text-2xl font-black mt-2" style={{ color: plan.color }}>
                      {fmt(price)} <span className="text-xs text-slate-500 font-normal">FCFA/mois</span>
                    </p>
                    <p className="text-[10px] text-slate-500 mt-1">{SERVICE_LABELS[selectedService]}</p>
                  </div>

                  {/* Features list */}
                  <div className="px-5 py-3 space-y-1.5 flex-1 border-t border-white/5">
                    {Object.entries(plan.features || {}).filter(([k]) => featureLabels[k]).map(([key, value]) => (
                      <div key={key} className="flex items-center gap-2 text-[11px]">
                        {typeof value === "boolean" ? (
                          value ? <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" /> : <X className="w-3.5 h-3.5 text-slate-200 flex-shrink-0" />
                        ) : (
                          <span className="text-sky-400 font-bold w-8 text-right flex-shrink-0">{value === -1 ? "\u221e" : value}</span>
                        )}
                        <span className={value ? "text-slate-300" : "text-slate-600"}>{featureLabels[key]}</span>
                      </div>
                    ))}
                  </div>

                  {/* Action buttons */}
                  <div className="p-4 border-t border-white/5 space-y-2">
                    {isCurrentPlan ? (
                      <div className="text-center py-2 text-sm text-emerald-400 font-semibold flex items-center justify-center gap-1.5">
                        <Check className="w-4 h-4" /> Votre formule actuelle
                      </div>
                    ) : isSuperAdmin ? (
                      <Button onClick={() => initiatePayment(planId, selectedService)} className="w-full text-sm font-bold" style={{ background: plan.color }} data-testid={`pay-${planId}`}>
                        <CreditCard className="w-4 h-4 mr-1.5" /> Payer — {fmt(price)} FCFA
                      </Button>
                    ) : isApproved ? (
                      <Button onClick={() => initiatePayment(planId, selectedService)} className="w-full text-sm font-bold bg-emerald-500 hover:bg-emerald-400" data-testid={`pay-${planId}`}>
                        <CreditCard className="w-4 h-4 mr-1.5" /> Payer — {fmt(price)} FCFA
                      </Button>
                    ) : hasPendingForThis ? (
                      <div className="text-center py-2 text-sm text-amber-400 font-semibold flex items-center justify-center gap-1.5">
                        <Hourglass className="w-4 h-4" /> En attente de validation
                      </div>
                    ) : (
                      <Button onClick={() => submitRequest(planId, selectedService)} disabled={submitting || !!pendingRequest}
                        className="w-full text-sm font-bold" style={{ background: plan.color }} data-testid={`request-${planId}`}>
                        {submitting ? <RefreshCw className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
                        {pendingRequest ? "Demande deja soumise" : `Demander — ${fmt(price)} FCFA`}
                      </Button>
                    )}
                    {plan.features?.trial_days > 0 && !isCurrentPlan && !hasPendingForThis && (
                      <p className="text-[10px] text-center text-slate-500">Essai gratuit de {plan.features.trial_days} jours inclus</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ===== REQUESTS TAB (SuperAdmin) ===== */}
        {activeTab === "requests" && isSuperAdmin && (
          <div className="space-y-3" data-testid="requests-list">
            {requests.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle2 className="w-10 h-10 text-emerald-500/30 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Aucune demande en attente.</p>
              </div>
            ) : requests.map(req => {
              const planColor = PLAN_COLORS[req.plan_id] || "#64748b";
              const isPending = req.status === "pending_approval";
              const isApprovedReq = req.status === "approved";
              return (
                <div key={req.id} className={`bg-slate-900/60 border rounded-xl p-5 ${isPending ? "border-amber-500/20" : "border-emerald-500/20"}`} data-testid={`request-${req.id}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isPending ? "bg-amber-500/100/10" : "bg-emerald-500/10"}`}>
                      {isPending ? <Hourglass className="w-6 h-6 text-amber-400" /> : <CheckCircle2 className="w-6 h-6 text-emerald-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-white">{req.pharmacy_name || req.pharmacy_email}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: planColor + "20", color: planColor }}>{req.plan_name}</span>
                        <span className="text-[10px] text-slate-500">{SERVICE_LABELS[req.service_type] || req.service_type}</span>
                        <span className="text-xs font-bold text-white">{fmt(req.price)} FCFA/mois</span>
                        <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${isPending ? "bg-amber-500/100/20 text-amber-400 border border-amber-500/30" : "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"}`}>
                          {isPending ? "EN ATTENTE" : "APPROUVEE"}
                        </span>
                      </div>
                      <p className="text-[10px] text-slate-500 mt-1">Soumise le {new Date(req.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {isPending && (
                        <>
                          <Button onClick={() => actionRequest(req.id, "approve")} size="sm"
                            className="bg-emerald-500 hover:bg-emerald-400 text-white text-xs" data-testid={`approve-${req.id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approuver
                          </Button>
                          <Button onClick={() => { const reason = prompt("Raison du refus (optionnel):"); actionRequest(req.id, "reject", reason || ""); }} size="sm"
                            variant="outline" className="border-red-500/20 text-red-400 hover:bg-red-500/100/100/10 text-xs" data-testid={`reject-${req.id}`}>
                            <XCircle className="w-3.5 h-3.5 mr-1" /> Refuser
                          </Button>
                        </>
                      )}
                      {isApprovedReq && (
                        <>
                          <select data-testid={`payment-method-${req.id}`} defaultValue="cash"
                            className="bg-slate-800 border border-white/10 rounded-lg text-white text-xs h-8 px-2" id={`pm-${req.id}`}>
                            <option value="cash">Especes</option>
                            <option value="virement">Virement</option>
                            <option value="cheque">Chèque</option>
                          </select>
                          <Button onClick={() => { const pm = document.getElementById(`pm-${req.id}`)?.value || "cash"; activateManually(req.id, pm); }} size="sm"
                            className="bg-sky-500 hover:bg-sky-400 text-white text-xs" data-testid={`activate-${req.id}`}>
                            <Zap className="w-3.5 h-3.5 mr-1" /> Activer
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ===== CLIENTS TAB ===== */}
        {activeTab === "clients" && isSuperAdmin && (
          <div className="space-y-3" data-testid="clients-list">
            {clients.length === 0 ? (
              <div className="text-center py-12 text-sm text-slate-500">Aucun abonnement attribue.</div>
            ) : clients.map(sub => {
              const isExpanded = expandedClient === sub.pharmacy_id;
              const planColor = PLAN_COLORS[sub.plan_id] || "#64748b";
              return (
                <div key={sub.pharmacy_id} className="bg-slate-900/60 border border-white/5 rounded-xl overflow-hidden" data-testid={`client-${sub.pharmacy_id}`}>
                  <div className="p-4 flex items-center gap-4 cursor-pointer hover:bg-white/5" onClick={() => setExpandedClient(isExpanded ? null : sub.pharmacy_id)}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: planColor + "20" }}>
                      <Crown className="w-5 h-5" style={{ color: planColor }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-white truncate">{sub.client_name || sub.client_email || sub.pharmacy_id}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: planColor + "20", color: planColor }}>{sub.plan_name}</span>
                        <span className="text-[10px] text-slate-500">{SERVICE_LABELS[sub.service_type] || sub.service_type}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_STYLES[sub.status] || STATUS_STYLES.active}`}>{sub.status}</span>
                      </div>
                    </div>
                    <p className="text-sm font-bold text-white">{fmt(sub.price)} <span className="text-[10px] text-slate-500">FCFA/mois</span></p>
                    <div className="flex items-center gap-2">
                      <Button onClick={(e) => { e.stopPropagation(); toggleService(sub.pharmacy_id, sub.status === "active" ? "suspended" : "active"); }}
                        size="sm" variant="outline" data-testid={`toggle-service-${sub.pharmacy_id}`}
                        className={`text-xs border ${sub.status === "active" ? "border-red-500/20 text-red-400 hover:bg-red-500/100/100/10" : "border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/10"}`}>
                        {sub.status === "active" ? <><ToggleRight className="w-3.5 h-3.5 mr-1" /> Suspendre</> : <><ToggleLeft className="w-3.5 h-3.5 mr-1" /> Activer</>}
                      </Button>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="border-t border-white/5 p-4">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-3">Options activables / desactivables</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {Object.entries(sub.features || {}).filter(([, v]) => typeof v === "boolean").map(([key, value]) => {
                          const Icon = FEATURE_ICONS[key] || Settings;
                          return (
                            <button key={key} onClick={() => toggleFeature(sub.pharmacy_id, key, value)} data-testid={`toggle-${key}-${sub.pharmacy_id}`}
                              className={`flex items-center gap-2 p-2.5 rounded-lg border transition-all text-left ${value ? "bg-emerald-500/10 border-emerald-500/20" : "bg-slate-900/40 border-white/5 opacity-60"}`}>
                              <Icon className={`w-4 h-4 flex-shrink-0 ${value ? "text-emerald-400" : "text-slate-500"}`} />
                              <span className={`text-xs ${value ? "text-white font-medium" : "text-slate-500"}`}>{featureLabels[key] || key}</span>
                              {value ? <Check className="w-3.5 h-3.5 text-emerald-400 ml-auto" /> : <X className="w-3.5 h-3.5 text-slate-600 ml-auto" />}
                            </button>
                          );
                        })}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
                        {Object.entries(sub.features || {}).filter(([, v]) => typeof v === "number").map(([key, value]) => (
                          <div key={key} className="bg-slate-900/40 border border-white/5 rounded-lg p-2.5">
                            <p className="text-[10px] text-slate-500">{featureLabels[key] || key}</p>
                            <p className="text-sm font-bold text-white">{value === -1 ? "Illimite" : value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </LogLayout>
  );
}
